/**
 * Модалка медиа топика — табы Фото / Файлы / Ссылки + lightbox для фото.
 *
 * Открывается тапом на header чата. Клик по элементу → закрывает модалку
 * и скроллит к исходному сообщению через jumpToMessage callback.
 *
 * Правый клик / long-press → context menu с действиями
 * (Перейти к сообщению, Сохранить как, Скопировать ссылку).
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { openSimpleSheet } from "@/components/chatVanillaMenu";
import { Spinner } from "@/components/Spinner";
import { PhotoLightbox, type LightboxImage } from "@/components/PhotoLightbox";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchTopicMedia, chatAttachmentUrl, deleteChatMessage,
} from "@/api/dashboard";
import { parseBackendIso } from "@/lib/presence";


type CtxItem =
  | { kind: "photo"; attachmentId: number; messageId: number; filename: string; mime: string | null; authorUserId: string | null }
  | { kind: "file"; attachmentId: number; messageId: number; filename: string; mime: string | null; authorUserId: string | null }
  | { kind: "link"; messageId: number; url: string; authorUserId: string | null };


function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso: string): string {
  if (!iso) return "";
  const d = parseBackendIso(iso);
  if (!d || Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}


type Tab = "photos" | "files" | "voices" | "links";


export function TopicMediaModal({ topicId, topicName, onClose, onJump }: {
  topicId: number;
  topicName: string;
  onClose: () => void;
  onJump: (messageId: number) => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("photos");
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const longPressRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);

  const deleteMut = useMutation({
    mutationFn: (messageId: number) => deleteChatMessage(messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/media", topicId] });
      qc.invalidateQueries({ queryKey: ["chat/messages"] });
    },
  });

  async function copyImageToClipboard(url: string): Promise<boolean> {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      // Если не PNG — конвертируем через canvas в PNG (Clipboard API часто хочет PNG)
      let finalBlob = blob;
      if (blob.type !== "image/png") {
        const img = new Image();
        const objUrl = URL.createObjectURL(blob);
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("img load failed"));
          img.src = objUrl;
        });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no canvas ctx");
        ctx.drawImage(img, 0, 0);
        finalBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(b => b ? resolve(b) : reject(new Error("toBlob failed")), "image/png");
        });
        URL.revokeObjectURL(objUrl);
      }
      await navigator.clipboard.write([new ClipboardItem({ "image/png": finalBlob })]);
      return true;
    } catch {
      return false;
    }
  }

  function openCtxMenu(item: CtxItem, x: number, y: number, activeEl?: HTMLElement | null) {
    const it = item;
    const url = it.kind === "link" ? it.url : chatAttachmentUrl(it.attachmentId);
    const isImage = it.kind === "photo" || (it.kind === "file" && (it.mime || "").toLowerCase().startsWith("image/"));
    const canDelete = !!user && (
      (it.authorUserId && user.id === it.authorUserId) || user.is_superuser
    );

    const items: Array<{ icon: string; label: string; onTap: () => void; danger?: boolean }> = [];
    items.push({ icon: "check", label: "Перейти к сообщению", onTap: () => handleClickItem(it.messageId) });
    if (it.kind !== "link") {
      const isMobile = window.innerWidth < 900;
      items.push({ icon: "save", label: isMobile ? "Сохранить к себе" : "Сохранить как…", onTap: async () => {
        const filename = it.filename || "file";
        if (isMobile && (navigator as any).canShare && (navigator as any).share) {
          try {
            const resp = await fetch(url, { credentials: "include" });
            const blob = await resp.blob();
            const file = new File([blob], filename, { type: blob.type });
            if ((navigator as any).canShare({ files: [file] })) {
              await (navigator as any).share({ files: [file], title: filename });
              return;
            }
          } catch (err) {
            console.error("[save] Web Share failed:", err);
          }
        }
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }});
    }
    if (isImage) {
      items.push({ icon: "copy", label: "Скопировать изображение", onTap: async () => {
        const ok = await copyImageToClipboard(url);
        if (!ok) alert("Не удалось скопировать изображение");
      }});
    }
    items.push({ icon: "external", label: "Скопировать ссылку", onTap: () => {
      const shareUrl = it.kind === "link" ? it.url : window.location.origin + url;
      copyToClipboard(shareUrl);
    }});
    if (it.kind === "link") {
      items.push({ icon: "external", label: "Открыть в новой вкладке", onTap: () => {
        window.open(it.url, "_blank", "noopener,noreferrer");
      }});
    }
    items.push({ icon: "reply", label: "Переслать", onTap: () => {
      const link = `${window.location.origin}/v2/chat?topic=${topicId}&jump=${it.messageId}`;
      copyToClipboard(link);
      alert("Ссылка на сообщение скопирована — вставьте в чат для пересылки");
    }});
    if (canDelete) {
      items.push({ icon: "trash", label: "Удалить", danger: true, onTap: () => {
        if (!window.confirm("Удалить сообщение?")) return;
        deleteMut.mutate(it.messageId);
      }});
    }
    openSimpleSheet({ items, isMobile: window.innerWidth < 900, activeEl, x, y });
  }
  function copyToClipboard(text: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }
  function onTouchStartItem(e: React.TouchEvent, item: CtxItem) {
    if (longPressRef.current) window.clearTimeout(longPressRef.current);
    longPressFiredRef.current = false;
    const t = e.touches[0];
    const x = t.clientX, y = t.clientY;
    const activeEl = e.currentTarget as HTMLElement;
    longPressRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      openCtxMenu(item, x, y, activeEl);
      if ("vibrate" in navigator) navigator.vibrate?.(15);
    }, 400);
  }
  function onTouchEndItem() {
    if (longPressRef.current) { window.clearTimeout(longPressRef.current); longPressRef.current = null; }
  }

  const q = useQuery({
    queryKey: ["chat/media", topicId],
    queryFn: () => fetchTopicMedia(topicId),
    staleTime: 30_000,
  });

  // Esc → закрыть модалку (lightbox сам обрабатывает свои keys)
  useEffect(() => {
    if (lightboxIdx !== null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, onClose]);

  function handleClickItem(messageId: number) {
    onJump(messageId);
    onClose();
  }

  const photos = q.data?.photos ?? [];
  const files = q.data?.files ?? [];
  const voices = q.data?.voices ?? [];
  const links = q.data?.links ?? [];
  const counts = { photos: photos.length, files: files.length, voices: voices.length, links: links.length };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        // Закрываем ТОЛЬКО на левый клик по backdrop'у (не на правый и не
        // на средний). Иначе правый клик где-то рядом с модалкой случайно
        // закрывал её — TG так не делает.
        if (e.button === 0 && e.target === e.currentTarget) onClose();
      }}
      onContextMenu={(e) => {
        // Блокируем браузерное контекст-меню на пустой зоне backdrop'а
        // и НЕ закрываем модалку. Контекст-меню внутри tile'ов работает
        // через свой onContextMenu в плитке.
        e.preventDefault();
      }}
    >
      <div className="modal-window media-modal" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {topicName}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--mute)" }}>
              {counts.photos} фото · {counts.files} файлов · {counts.voices} голосовых · {counts.links} ссылок
            </div>
          </div>
          <button className="modal-close" onClick={onClose} title="Закрыть (Esc)" style={{ flexShrink: 0 }}>
            <Icon name="x" size={18}/>
          </button>
        </div>

        <div className="media-tabs">
          {(["photos", "files", "voices", "links"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              className={`media-tab${tab === t ? " is-active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t === "photos" && `Фото · ${counts.photos}`}
              {t === "files" && `Файлы · ${counts.files}`}
              {t === "voices" && `Голосовые · ${counts.voices}`}
              {t === "links" && `Ссылки · ${counts.links}`}
            </button>
          ))}
        </div>

        <div className="modal-body" style={{ padding: 12 }}>
          {q.isLoading && <Spinner size="md"/>}
          {q.error && <div style={{ padding: 20, color: "var(--brick)" }}>Ошибка: {(q.error as Error).message}</div>}

          {tab === "photos" && !q.isLoading && (
            photos.length === 0
              ? <div className="media-empty">Фото в этом топике пока нет</div>
              : (
                <div className="media-grid">
                  {photos.map((p, i) => (
                    <button
                      key={p.attachment_id}
                      type="button"
                      className="media-tile"
                      data-tile-kind="photo"
                      data-tile-aid={p.attachment_id}
                      data-tile-message-id={p.message_id}
                      data-tile-filename={p.filename}
                      data-tile-mime={p.mime || ""}
                      data-tile-author={p.author_user_id || ""}
                      onClick={() => { if (longPressFiredRef.current) return; setLightboxIdx(i); }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        openCtxMenu(
                          { kind: "photo", attachmentId: p.attachment_id, messageId: p.message_id, filename: p.filename, mime: p.mime, authorUserId: p.author_user_id ?? null },
                          e.clientX, e.clientY, e.currentTarget as HTMLElement,
                        );
                      }}
                      onTouchStart={(e) => onTouchStartItem(e, { kind: "photo", attachmentId: p.attachment_id, messageId: p.message_id, filename: p.filename, mime: p.mime, authorUserId: p.author_user_id ?? null })}
                      onTouchEnd={onTouchEndItem}
                      onTouchMove={onTouchEndItem}
                      title={`${p.filename} · ${p.author_name}`}
                    >
                      <img src={chatAttachmentUrl(p.attachment_id)} alt={p.filename} loading="lazy" draggable={false}/>
                    </button>
                  ))}
                </div>
              )
          )}

          {tab === "files" && !q.isLoading && (
            files.length === 0
              ? <div className="media-empty">Файлов в этом топике пока нет</div>
              : (
                <div className="media-files">
                  {files.map((f) => (
                    <a
                      key={f.attachment_id}
                      className="media-file"
                      href={chatAttachmentUrl(f.attachment_id)}
                      download={f.filename}
                      data-tile-kind="file"
                      data-tile-aid={f.attachment_id}
                      data-tile-message-id={f.message_id}
                      data-tile-filename={f.filename}
                      data-tile-mime={f.mime || ""}
                      data-tile-author={f.author_user_id || ""}
                      onClick={(e) => { e.stopPropagation(); }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        openCtxMenu(
                          { kind: "file", attachmentId: f.attachment_id, messageId: f.message_id, filename: f.filename, mime: f.mime, authorUserId: f.author_user_id ?? null },
                          e.clientX, e.clientY, e.currentTarget as HTMLElement,
                        );
                      }}
                      onTouchStart={(e) => onTouchStartItem(e, { kind: "file", attachmentId: f.attachment_id, messageId: f.message_id, filename: f.filename, mime: f.mime, authorUserId: f.author_user_id ?? null })}
                      onTouchEnd={onTouchEndItem}
                      onTouchMove={onTouchEndItem}
                    >
                      <Icon name="file" size={20}/>
                      <div className="media-file-body">
                        <div className="media-file-name" title={f.filename}>{f.filename}</div>
                        <div className="media-file-meta">
                          {fmtBytes(f.size_bytes)} · {f.author_name} · {fmtDate(f.created_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClickItem(f.message_id); }}
                        title="Перейти к сообщению"
                      >
                        →
                      </button>
                    </a>
                  ))}
                </div>
              )
          )}

          {tab === "voices" && !q.isLoading && (
            voices.length === 0
              ? <div className="media-empty">Голосовых в этом топике пока нет</div>
              : (
                <div className="media-files">
                  {voices.map((v) => (
                    <a
                      key={v.attachment_id}
                      className="media-file"
                      href={chatAttachmentUrl(v.attachment_id)}
                      download={v.filename}
                      data-tile-kind="file"
                      data-tile-aid={v.attachment_id}
                      data-tile-message-id={v.message_id}
                      data-tile-filename={v.filename}
                      data-tile-mime={v.mime || ""}
                      data-tile-author={v.author_user_id || ""}
                      onClick={(e) => { e.stopPropagation(); }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        openCtxMenu(
                          { kind: "file", attachmentId: v.attachment_id, messageId: v.message_id, filename: v.filename, mime: v.mime, authorUserId: v.author_user_id ?? null },
                          e.clientX, e.clientY, e.currentTarget as HTMLElement,
                        );
                      }}
                    >
                      <Icon name="mic" size={20}/>
                      <div className="media-file-body">
                        <div className="media-file-name" title={v.filename}>
                          {v.duration_sec ? `${Math.floor(v.duration_sec / 60)}:${String(Math.floor(v.duration_sec % 60)).padStart(2, "0")}` : v.filename}
                        </div>
                        {v.transcription && (
                          <div className="media-file-meta" style={{ fontStyle: "italic", whiteSpace: "normal", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                            «{v.transcription}»
                          </div>
                        )}
                        <div className="media-file-meta">
                          {v.author_name} · {fmtDate(v.created_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClickItem(v.message_id); }}
                        title="Перейти к сообщению"
                      >
                        →
                      </button>
                    </a>
                  ))}
                </div>
              )
          )}

                    {tab === "links" && !q.isLoading && (
            links.length === 0
              ? <div className="media-empty">Ссылок в этом топике пока нет</div>
              : (
                <div className="media-files">
                  {links.map((l, i) => (
                    <div
                      key={`${l.message_id}-${i}`}
                      className="media-file"
                      style={{ cursor: "default" }}
                      data-tile-kind="link"
                      data-tile-message-id={l.message_id}
                      data-tile-url={l.url}
                      data-tile-author={l.author_user_id || ""}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        openCtxMenu({ kind: "link", messageId: l.message_id, url: l.url, authorUserId: l.author_user_id ?? null }, e.clientX, e.clientY, e.currentTarget as HTMLElement);
                      }}
                      onTouchStart={(e) => onTouchStartItem(e, { kind: "link", messageId: l.message_id, url: l.url, authorUserId: l.author_user_id ?? null })}
                      onTouchEnd={onTouchEndItem}
                      onTouchMove={onTouchEndItem}
                    >
                      <Icon name="external" size={18}/>
                      <div className="media-file-body">
                        <a
                          className="media-link"
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {l.url}
                        </a>
                        <div className="media-file-meta">
                          {l.author_name} · {fmtDate(l.created_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={(e) => { e.stopPropagation(); handleClickItem(l.message_id); }}
                        title="Перейти к сообщению"
                      >
                        →
                      </button>
                    </div>
                  ))}
                </div>
              )
          )}
        </div>
      </div>

      {lightboxIdx !== null && photos[lightboxIdx] && (
        <PhotoLightbox
          images={photos.map<LightboxImage>(p => ({
            src: chatAttachmentUrl(p.attachment_id),
            filename: p.filename,
            caption: `${p.author_name} · ${fmtDate(p.created_at)}`,
            onJump: () => handleClickItem(p.message_id),
          }))}
          idx={lightboxIdx}
          setIdx={setLightboxIdx as (i: number) => void}
          onClose={() => setLightboxIdx(null)}
        />
      )}

    </div>
  );
}
